import React, { useState } from "react";
import { motion } from "framer-motion";
import { 
  Mail,
  MessageSquare,
  MapPin,
  Clock,
  Send,
  X,
  User,
  MessageCircle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  BookOpen
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Link } from "react-router-dom";

export default function Contact() {
  const [contactForm, setContactForm] = useState({ name: "", email: "", message: "" });
  const [feedbackForm, setFeedbackForm] = useState({ rating: 5, feedback: "" });
  const [reportForm, setReportForm] = useState({ type: "", description: "" });
  
  const [showContactModal, setShowContactModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const contactInfo = [
    {
      icon: Mail,
      title: "Email Support",
      value: "support@vreels.com",
      description: "Get help with your account, features, or technical issues",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: MapPin,
      title: "Office Location",
      value: "Saginaw, TX",
      description: "Visit us at our headquarters",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: Clock,
      title: "Support Hours",
      value: "24/7 Available",
      description: "We're here to help around the clock",
      color: "from-orange-500 to-red-500"
    }
  ];

  const faqData = [
    {
      question: "How do I create my first reel?",
      answer: "Creating reels is simple! Use our built-in video editor to record or upload content, add effects and music, then share with your audience."
    },
    {
      question: "What are Pixs and how do I share them?",
      answer: "Pixs are shareable moments and images. You can upload photos, create collections, and share them with the community easily."
    },
    {
      question: "How does the shopping feature work?",
      answer: "Browse products from creators and verified brands, add items to your cart, and checkout securely. All transactions are protected."
    },
    {
      question: "Can I create private pix pouches?",
      answer: "Yes! You can create both public and private pix pouches to organize your favorite pixs and content."
    },
    {
      question: "Is VREELS free to use?",
      answer: "Yes, VREELS is free to download and use."
    }
  ];

  const handleFormSubmit = async (formType) => {
    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Reset forms
      if (formType === 'contact') {
        setContactForm({ name: "", email: "", message: "" });
        setShowContactModal(false);
      } else if (formType === 'feedback') {
        setFeedbackForm({ rating: 5, feedback: "" });
        setShowFeedbackModal(false);
      } else if (formType === 'report') {
        setReportForm({ type: "", description: "" });
        setShowReportModal(false);
      }
      
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const Modal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-background rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b">
            <h3 className="text-xl font-semibold">{title}</h3>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="p-6">
            {children}
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="py-16 bg-gradient-to-br from-background to-secondary/20">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            Get In{" "}
            <span className="bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
              Touch
            </span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Have questions about creating reels, sharing pixs, or shopping? We're here to help you succeed on VREELS.
          </p>
        </motion.div>

        {/* Contact Information */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {contactInfo.map((info, index) => {
            const Icon = info.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="group h-full hover:shadow-lg transition-all duration-300 hover:scale-105 bg-gradient-to-br from-card to-card/50 border-2 hover:border-primary/30 relative overflow-hidden">
                  {/* Background Pattern */}
                  <div className="absolute inset-0 overflow-hidden">
                    <div className={`absolute -right-8 -top-8 w-32 h-32 bg-gradient-to-br ${info.color} opacity-5 rounded-full blur-2xl`} />
                    {info.title === "Email Support" && (
                      <>
                        {/* Email envelope patterns */}
                        <div className="absolute top-4 right-4 w-10 h-7 bg-gradient-to-br from-blue-500/30 to-cyan-500/30 rounded-md border border-white/20">
                          <div className="absolute inset-1 border-b border-white/20"></div>
                          <div className="absolute top-1 left-1 right-1 h-0.5 bg-white/20 rounded-full"></div>
                        </div>
                        <div className="absolute top-8 right-8 w-8 h-6 bg-gradient-to-br from-blue-400/25 to-cyan-400/25 rounded-md border border-white/15"></div>
                        <div className="absolute bottom-6 right-6 w-6 h-4 bg-gradient-to-br from-blue-300/30 to-cyan-300/30 rounded-md border border-white/20"></div>
                        {/* @ symbol dots */}
                        <div className="absolute bottom-4 right-12 w-3 h-3 bg-blue-400/30 rounded-full"></div>
                        <div className="absolute bottom-2 right-10 w-2 h-2 bg-cyan-400/30 rounded-full"></div>
                      </>
                    )}
                    {info.title === "Office Location" && (
                      <>
                        {/* Map pin patterns */}
                        <div className="absolute top-4 right-4 w-8 h-10 bg-gradient-to-br from-purple-500/30 to-pink-500/30 rounded-full rounded-b-none border border-white/20">
                          <div className="w-3 h-3 bg-white/40 rounded-full mx-auto mt-2"></div>
                        </div>
                        <div className="absolute top-8 right-10 w-6 h-8 bg-gradient-to-br from-purple-400/25 to-pink-400/25 rounded-full rounded-b-none border border-white/15">
                          <div className="w-2 h-2 bg-white/30 rounded-full mx-auto mt-1.5"></div>
                        </div>
                        <div className="absolute bottom-6 right-6 w-5 h-6 bg-gradient-to-br from-purple-300/30 to-pink-300/30 rounded-full rounded-b-none border border-white/20"></div>
                        {/* Location dots */}
                        <div className="absolute bottom-4 right-12 w-2 h-2 bg-purple-400/40 rounded-full"></div>
                        <div className="absolute bottom-8 right-8 w-1.5 h-1.5 bg-pink-400/40 rounded-full"></div>
                        <div className="absolute bottom-2 right-14 w-1.5 h-1.5 bg-purple-300/40 rounded-full"></div>
                      </>
                    )}
                    {info.title === "Support Hours" && (
                      <>
                        {/* Clock patterns */}
                        <div className="absolute top-4 right-4 w-10 h-10 bg-gradient-to-br from-orange-500/30 to-red-500/30 rounded-full border border-white/20 flex items-center justify-center">
                          <div className="w-1 h-3 bg-white/40 rounded-full"></div>
                          <div className="w-2 h-1 bg-white/40 rounded-full ml-0.5"></div>
                        </div>
                        <div className="absolute top-8 right-10 w-8 h-8 bg-gradient-to-br from-orange-400/25 to-red-400/25 rounded-full border border-white/15"></div>
                        <div className="absolute bottom-6 right-6 w-6 h-6 bg-gradient-to-br from-orange-300/30 to-red-300/30 rounded-full border border-white/20"></div>
                        {/* Time indicator dots */}
                        <div className="absolute bottom-4 right-12 w-2 h-2 bg-orange-400/40 rounded-full"></div>
                        <div className="absolute bottom-8 right-8 w-1.5 h-1.5 bg-red-400/40 rounded-full"></div>
                        <div className="absolute bottom-2 right-14 w-1.5 h-1.5 bg-orange-300/40 rounded-full"></div>
                        {/* 24/7 pattern */}
                        <div className="absolute bottom-0 right-0 w-full h-4 bg-gradient-to-r from-transparent via-orange-500/10 to-red-500/10">
                          <div className="flex h-full items-center justify-end space-x-1 pr-3">
                            <div className="w-1 h-1 bg-orange-400/30 rounded-full"></div>
                            <div className="w-1 h-1 bg-red-400/30 rounded-full"></div>
                            <div className="w-1 h-1 bg-orange-400/30 rounded-full"></div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  
                  <CardContent className="p-6 text-center relative z-10">
                    <div className={`mx-auto w-12 h-12 bg-gradient-to-br ${info.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2 group-hover:text-primary transition-colors duration-300">{info.title}</h3>
                    <p className="text-primary font-medium mb-2">{info.value}</p>
                    <p className="text-sm text-muted-foreground">{info.description}</p>
                  </CardContent>
                  
                  {/* Hover Effect Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-transparent to-black/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl" />
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Action Buttons grid commented out temporarily */}
        {false && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12"
        >
          {/* Cards commented */}
        </motion.div>
        )}

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
        >
          <Card className="max-w-4xl mx-auto">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl md:text-3xl">Frequently Asked Questions</CardTitle>
              <CardDescription className="text-lg">
                Quick answers to common questions about VREELS
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {faqData.map((faq, index) => (
                  <div key={index} className="border rounded-lg">
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                      className="w-full p-4 text-left flex items-center justify-between hover:bg-secondary/50 transition-colors duration-200"
                    >
                      <span className="font-medium">{faq.question}</span>
                      {expandedFaq === index ? (
                        <ChevronUp className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-muted-foreground" />
                      )}
                    </button>
                    {expandedFaq === index && (
                      <div className="p-4 pt-0 text-muted-foreground">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* More FAQs Link */}
        <div className="text-center mt-6">
          <Link to="/faqs">
            <Button variant="outline" className="inline-flex items-center gap-2 group hover:bg-primary/10">
              <BookOpen className="h-5 w-5 text-primary group-hover:animate-pulse" />
              More FAQs
            </Button>
          </Link>
        </div>

        {/* Success Message */}


        {/* Modals */}
        {/* <Modal isOpen={showContactModal} onClose={() => setShowContactModal(false)} title="Contact Us">
          <div className="space-y-4">
            <Input
              placeholder="Your Name"
              value={contactForm.name}
              onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
            />
            <Input
              type="email"
              placeholder="Your Email"
              value={contactForm.email}
              onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
            />
            <textarea
              className="w-full min-h-[120px] p-3 border border-border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Your Message"
              value={contactForm.message}
              onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
            />
            <Button 
              onClick={() => handleFormSubmit('contact')} 
              disabled={isLoading}
              className="w-full"
            >
              {isLoading ? "Sending..." : "Send Message"}
              <Send className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </Modal>

        <Modal isOpen={showFeedbackModal} onClose={() => setShowFeedbackModal(false)} title="Send Feedback">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Rating</label>
              <div className="flex space-x-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setFeedbackForm({...feedbackForm, rating: star})}
                    className={`text-2xl ${star <= feedbackForm.rating ? 'text-yellow-500' : 'text-gray-300'}`}
                  >
                    ★
                  </button>
                ))}
              </div>
            </div>
            <textarea
              className="w-full min-h-[120px] p-3 border border-border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Your Feedback"
              value={feedbackForm.feedback}
              onChange={(e) => setFeedbackForm({...feedbackForm, feedback: e.target.value})}
            />
            <Button 
              onClick={() => handleFormSubmit('feedback')} 
              disabled={isLoading}
              className="w-full"
            >
              {isLoading ? "Sending..." : "Submit Feedback"}
              <Send className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </Modal>

        <Modal isOpen={showReportModal} onClose={() => setShowReportModal(false)} title="Report Issue">
          <div className="space-y-4">
            <select
              className="w-full p-3 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              value={reportForm.type}
              onChange={(e) => setReportForm({...reportForm, type: e.target.value})}
            >
              <option value="">Select Issue Type</option>
              <option value="technical">Technical Problem</option>
              <option value="content">Inappropriate Content</option>
              <option value="account">Account Issue</option>
              <option value="other">Other</option>
            </select>
            <textarea
              className="w-full min-h-[120px] p-3 border border-border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Describe the issue..."
              value={reportForm.description}
              onChange={(e) => setReportForm({...reportForm, description: e.target.value})}
            />
            <Button 
              onClick={() => handleFormSubmit('report')} 
              disabled={isLoading}
              variant="destructive"
              className="w-full"
            >
              {isLoading ? "Reporting..." : "Submit Report"}
              <Send className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </Modal> */}
      </div>
    </section>
  );
}